import "./graph.css";

import React, { useEffect, useMemo, useCallback, useState } from "react";

import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Container from "react-bootstrap/Container";
import Dropdown from "react-bootstrap/Dropdown";
import Spinner from "react-bootstrap/Spinner";
import { Form } from "react-bootstrap";
import { patientActions } from "../../actions/PatientActions";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

export default function GraphMenu({ adm, showGeneticToggle = false }) {
  const dispatch = useDispatch();
  const dropdownContainer =
    typeof document !== "undefined" ? document.body : null;
  const microId = useSelector((state) => state.patient.micro_id);
  const ptObj = useSelector((state) => state.patient.patient);
  const tree_exists = useSelector((state) => state.patient.tree.tree);
  const overlap_constraint = useSelector(
    (state) => state.patient.tree.overlap_constraint
  );
  const constraintLoading = useSelector(
    (state) => state.patient.tree.constraint_loading
  );
  const snp_dist = useSelector((state) => state.patient.snp_distance);

  const graphInfo = useSelector((state) => state.patient.graph.info);
  const edgeWidthParam = useSelector(
    (state) => state.patient.graph.edge_width_param
  );
  const edgeColourParam = useSelector(
    (state) => state.patient.graph.edge_colour_param
  );
  const layers = useSelector((state) => state.patient.layers);

  const reduxSelectedContam = useSelector(
    (state) => state.patient.patient.contam_period
  );
  const reduxSelectedIncubation = useSelector(
    (state) => state.patient.patient.incubation
  );
  const reduxSelectedInfPrior = useSelector(
    (state) => state.patient.patient.inf_period_prior
  );
  const reduxSelectedInfAfter = useSelector(
    (state) => state.patient.patient.inf_period_after
  );
  const reduxSelectedTTB = useSelector(
    (state) => state.patient.patient.test_time_buffer
  );
  const overlapLevel = useSelector(
    (state) => state.patient.patient.overlap_level || "ward"
  );
  const graphNodes = useSelector((state) => state.patient.graph.nodes || []);
  const graphEdges = useSelector((state) => state.patient.graph.edges || []);
  const nameMap = useSelector((state) => state.patient.tree.name_map || {});
  const nameMapSnp2 = useSelector(
    (state) => state.patient.tree.name_map_snp2 || {}
  );
  const [fullNameMap, setFullNameMap] = useState({
    ...nameMapSnp2,
    ...nameMap,
  });
  const currentGraphConstraints = useSelector(
    (state) => state.patient.graph.constrain || []
  );
  const graphPruned = useSelector(
    (state) => state.patient.graph.pruneForPhylos
  );
  const graphHighlightOn = useSelector(
    (state) => state.patient.graph.highlightPhylos
  );
  const currentTreeConstraints = useSelector(
    (state) => state.patient.tree.constrain || []
  );

  useEffect(() => {
    setFullNameMap({ ...nameMapSnp2, ...nameMap });
  }, [microId, snp_dist]);

  useEffect(() => {
    setFullNameMap((prev) => ({
      ...prev,
      ...nameMapSnp2,
      ...nameMap,
    }));
  }, [nameMap, nameMapSnp2]);

  const graphNodeIdSet = useMemo(
    () => new Set(graphNodes.map((n) => String(n.id))),
    [graphNodes]
  );

  const geneticGraphMicroIds = useMemo(() => {
    const ids = new Set();
    Object.values(fullNameMap || {}).forEach((entry) => {
      if (Array.isArray(entry)) {
        const [, microId] = entry;
        if (
          microId !== null &&
          microId !== undefined &&
          graphNodeIdSet.has(String(microId))
        ) {
          ids.add(String(microId));
        }
      }
    });
    return Array.from(ids);
  }, [fullNameMap, graphNodeIdSet]);

  const buildAdjacency = (edges) => {
    const adj = new Map();
    (edges || []).forEach(({ source, target }) => {
      const s = String(source);
      const t = String(target);
      if (!adj.has(s)) adj.set(s, []);
      if (!adj.has(t)) adj.set(t, []);
      adj.get(s).push(t);
      adj.get(t).push(s);
    });
    return adj;
  };

  const computeConstraintSet = (rootId, targetIds, edges) => {
    const adj = buildAdjacency(edges);
    const keep = new Set();
    const root = String(rootId);
    keep.add(root);

    const shortestPath = (start, target) => {
      if (start === target) return [start];
      const queue = [start];
      const visited = new Set([start]);
      const parent = new Map();
      while (queue.length) {
        const current = queue.shift();
        const neighbours = adj.get(current) || [];
        for (const next of neighbours) {
          if (visited.has(next)) continue;
          visited.add(next);
          parent.set(next, current);
          if (next === target) {
            const path = [next];
            let p = current;
            while (p) {
              path.push(p);
              p = parent.get(p);
            }
            return path.reverse();
          }
          queue.push(next);
        }
      }
      return null;
    };

    targetIds.forEach((targetId) => {
      const target = String(targetId);
      const path = shortestPath(root, target);
      if (path) {
        path.forEach((nodeId) => keep.add(nodeId));
      } else {
        keep.add(String(targetId));
      }
    });
    return keep;
  };

  const collectSeqIdsForMicroIds = useCallback(
    (idSet) => {
      const seqs = new Set();
      Object.entries(fullNameMap || {}).forEach(([seqId, entry]) => {
        if (Array.isArray(entry)) {
          const [, microId] = entry;
          if (
            microId !== null &&
            microId !== undefined &&
            idSet.has(String(microId)) &&
            seqId
          ) {
            seqs.add(seqId);
          }
        }
      });
      return Array.from(seqs);
    },
    [fullNameMap]
  );

  const graphConstraintSet = useMemo(() => {
    if (!overlap_constraint) return new Set();
    if (!geneticGraphMicroIds.length) return new Set([String(microId)]);
    return computeConstraintSet(microId, geneticGraphMicroIds, graphEdges);
  }, [overlap_constraint, geneticGraphMicroIds, graphEdges, microId]);

  const DEFAULTS = {
    layers: 3,
    contam: "28",
    incubation: "84",
    infPrior: "0",
    infAfter: "56",
    ttb: "1",
    overlapLevel: "ward",
    edgeWidth: "duration",
  };

  const contaminationPeriods = [
    "0",
    "7",
    "14",
    "21",
    "28",
    "35",
    "42",
    "49",
    "56",
    "63",
    "70",
    "77",
    "84",
    "91",
  ];
  const incubationPeriods = [
    "0",
    "7",
    "14",
    "21",
    "28",
    "35",
    "42",
    "49",
    "56",
    "63",
    "70",
    "77",
    "84",
    "91",
    "100",
    "150",
    "200",
    "365",
  ];
  const infectiousAfterPeriods = [
    "0",
    "7",
    "14",
    "21",
    "28",
    "35",
    "42",
    "49",
    "56",
    "60",
    "63",
    "65",
    "70",
    "75",
    "77",
    "80",
    "84",
    "85",
    "90",
    "91",
    "95",
    "100",
    "105",
    "110",
  ];
  const infectiousPriorPeriods = [
    "0",
    "1",
    "2",
    "3",
    "5",
    "7",
    "10",
    "14",
    "20",
    "28",
    "30",
    "40",
    "50",
    "60",
  ];
  const testBufferPeriods = [
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
  ];

  const selectedContam = String(reduxSelectedContam ?? DEFAULTS.contam);
  const selectedIncubation = String(reduxSelectedIncubation ?? DEFAULTS.incubation);
  const selectedInfPrior = String(reduxSelectedInfPrior ?? DEFAULTS.infPrior);
  const selectedInfAfter = String(reduxSelectedInfAfter ?? DEFAULTS.infAfter);
  const selectedTTB = String(reduxSelectedTTB ?? DEFAULTS.ttb);
  const selectedLayers = String(layers ?? DEFAULTS.layers);
  const selectedLevel = overlapLevel || DEFAULTS.overlapLevel;

  const sortKey = useCallback(
    (arr) => arr.map(String).sort().join("|"),
    []
  );

  const clearGraphConstraints = useCallback(() => {
    dispatch(patientActions.constrainGraph([], false));
    dispatch(patientActions.highlightPhylos([], false));
  }, [dispatch]);

  const clearConstraints = useCallback(() => {
    if (graphPruned || graphHighlightOn || currentGraphConstraints.length) {
      clearGraphConstraints();
    }
    if (currentTreeConstraints.length) {
      dispatch(patientActions.constrainTree([]));
    }
  }, [
    clearGraphConstraints,
    currentGraphConstraints.length,
    currentTreeConstraints.length,
    dispatch,
    graphHighlightOn,
    graphPruned,
  ]);

  const applyConstraints = useCallback(
    (constraintSet) => {
      const set =
        constraintSet instanceof Set
          ? constraintSet
          : new Set((constraintSet || []).map((id) => String(id)));
      const constraintList = Array.from(set);

      const newGraphKey = sortKey(constraintList);
      const currentGraphKey = sortKey(currentGraphConstraints);

      if (newGraphKey !== currentGraphKey || !graphPruned) {
        dispatch(patientActions.constrainGraph(constraintList, true));
      }
      if (!graphHighlightOn || newGraphKey !== currentGraphKey) {
        dispatch(patientActions.highlightPhylos(constraintList, true));
      }

      const seqIds = collectSeqIdsForMicroIds(set);
      const newTreeKey = sortKey(seqIds);
      const currentTreeKey = sortKey(currentTreeConstraints);
      if (newTreeKey !== currentTreeKey) {
        dispatch(patientActions.constrainTree(seqIds));
      }
    },
    [
      collectSeqIdsForMicroIds,
      currentGraphConstraints,
      currentTreeConstraints,
      dispatch,
      graphHighlightOn,
      graphPruned,
      sortKey,
    ]
  );

  const handleOverlapConstraintToggle = () => {
    const enabling = !overlap_constraint;
    dispatch(patientActions.constrainByOverlap(enabling));
    if (enabling) {
      if (!geneticGraphMicroIds.length) {
        dispatch(patientActions.constrainByOverlap(false));
        return;
      }
      const constraintSet = computeConstraintSet(
        microId,
        geneticGraphMicroIds,
        graphEdges
      );
      applyConstraints(constraintSet);
    } else {
      clearConstraints();
    }
  };

  useEffect(() => {
    if (!overlap_constraint) return;
    if (!geneticGraphMicroIds.length) {
      dispatch(patientActions.constrainByOverlap(false));
      clearConstraints();
      return;
    }
    applyConstraints(graphConstraintSet);
  }, [
    overlap_constraint,
    geneticGraphMicroIds,
    graphConstraintSet,
    applyConstraints,
    clearConstraints,
    dispatch,
  ]);

  useEffect(() => {
    if (overlap_constraint) return;
    if (!graphPruned && !graphHighlightOn && currentGraphConstraints.length === 0)
      return;
    clearGraphConstraints();
  }, [
    overlap_constraint,
    graphPruned,
    graphHighlightOn,
    currentGraphConstraints.length,
    clearGraphConstraints,
  ]);

  const handleToggle = () => {
    dispatch(patientActions.graphInfoToggled(!graphInfo));
  };

  const handleLayerSelect = (evt) => {
    const value = parseInt(evt, 10);
    if (!Number.isNaN(value)) {
      dispatch(patientActions.storeLayers(value));
    }
  };

  const handleEdgeWidth = (evt) => {
    dispatch(patientActions.storeEdgeWidthParam(evt));
  };

  const handleEdgeColour = (evt) => {
    dispatch(patientActions.storeEdgeColourParam(evt));
  };

  const handleLevelSelect = (evt) => {
    dispatch(patientActions.selectLevel(evt));
  };

  const handleContamSelect = (evt) => {
    dispatch(patientActions.selectContamPeriod(evt));
  };

  const handleIncubationSelect = (evt) => {
    dispatch(patientActions.selectIncubationPeriod(evt));
  };

  const handleInfPriorSelect = (evt) => {
    dispatch(patientActions.selectInfPeriodPrior(evt));
  };
  const handleInfAfterSelect = (evt) => {
    dispatch(patientActions.selectInfPeriodAfter(evt));
  };
  const handleTestTimeBufferSelect = (evt) => {
    dispatch(patientActions.selectTestTimeBuffer(evt));
  };

  const handleSnpDistanceSelect = async (evt) => {
    if (evt === snp_dist) return;
    dispatch(patientActions.storeSnpDistance(evt));
  };

  const handleResetAll = () => {
    dispatch(patientActions.graphInfoToggled(true));
    dispatch(patientActions.storeEdgeWidthParam(DEFAULTS.edgeWidth));
    dispatch(patientActions.storeEdgeColourParam("None"));
    dispatch(patientActions.storeLayers(DEFAULTS.layers));
    dispatch(patientActions.selectLevel(DEFAULTS.overlapLevel));

    dispatch(patientActions.selectContamPeriod(DEFAULTS.contam));
    dispatch(patientActions.selectIncubationPeriod(DEFAULTS.incubation));
    dispatch(patientActions.selectInfPeriodPrior(DEFAULTS.infPrior));
    dispatch(patientActions.selectInfPeriodAfter(DEFAULTS.infAfter));
    dispatch(patientActions.selectTestTimeBuffer(DEFAULTS.ttb));

    dispatch(patientActions.constrainTree([]));
    dispatch(patientActions.constrainByOverlap(false));
    dispatch(patientActions.constrainOverlap([]));
    clearGraphConstraints();
  };

  const PARAM_COLOURS = {
    infected: "#d1615d",
    atRisk: "#5778a4",
    contam: "darkOrange",
  };

  const ParamDot = ({ color }) => (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 9,
        height: 9,
        borderRadius: "50%",
        backgroundColor: color,
        marginRight: 8,
        marginBottom: 2,
        verticalAlign: "middle",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
      }}
    />
  );

  return (
    <Container
      fluid
      className="graph-menu-bar d-flex flex-row flex-wrap align-items-center gap-2"
    >
      <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleLevelSelect}
          container={dropdownContainer}
        >
          <Dropdown.Toggle className="blue-button" size="sm" as={Button}>
            <span style={{ fontWeight: 550 }}>Overlap Level</span>&nbsp;&nbsp;
            {selectedLevel[0].toUpperCase() + selectedLevel.slice(1)}
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="level-menu"
            title="Overlap level"
            variant="light"
            size="sm"
          >
            {[
              { key: "ward", label: "Ward" },
              { key: "specialty", label: "Specialty" },
              { key: "hospital", label: "Hospital" },
            ].map(({ key, label }) => (
              <Dropdown.Item
                key={key}
                className="custom-dropdown-item"
                eventKey={key}
                active={selectedLevel === key}
              >
                {label}
                {key === DEFAULTS.overlapLevel ? " (default)" : ""}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleLayerSelect}
          container={dropdownContainer}
        >
          <Dropdown.Toggle size="sm" className="blue-button">
            <span style={{ fontWeight: 550 }}>Graph Depth</span>&nbsp;&nbsp;
            {selectedLayers} layers
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="layer-menu"
            title="Number of shells"
            variant="light"
            size="sm"
          >
            {Array.from({ length: 4 }, (_, idx) => (
              <Dropdown.Item
                key={idx}
                className="custom-dropdown-item"
                eventKey={String(idx + 2)}
                active={String(idx + 2) === selectedLayers}
              >
                {idx + 2}
                {idx + 2 === DEFAULTS.layers ? " (default)" : ""}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleIncubationSelect}
          container={dropdownContainer}
        >
          <Dropdown.Toggle size="sm" className="blue-button" disabled={adm}>
            <ParamDot color={PARAM_COLOURS.atRisk} />
            <span style={{ fontWeight: 550 }}>Incubation Period</span>
            &nbsp;&nbsp;
            {selectedIncubation} days
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="incubation-menu"
            title="Incubation period"
            variant="light"
            size="sm"
          >
            {incubationPeriods.map((period) => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedIncubation === period}
                className="custom-dropdown-item"
                disabled={adm}
              >
                {period}
                {period === DEFAULTS.incubation ? " days (default)" : " days"}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleInfPriorSelect}
          container={dropdownContainer}
        >
          <Dropdown.Toggle size="sm" className="blue-button" disabled={adm}>
            <span style={{ fontWeight: 550 }}>Infectious (Pre-test)</span>
            &nbsp;&nbsp;
            {selectedInfPrior} days
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="infprior-menu"
            title="Infectious period prior test"
            variant="light"
            size="sm"
          >
            {infectiousPriorPeriods.map((period) => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedInfPrior === period}
                className="custom-dropdown-item"
              >
                {period}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div> */}

      <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleInfAfterSelect}
          container={dropdownContainer}
        >
          <Dropdown.Toggle size="sm" className="blue-button" disabled={adm}>
            <ParamDot color={PARAM_COLOURS.infected} />
            <span style={{ fontWeight: 550 }}>Infectious Period</span>
            &nbsp;&nbsp;
            {selectedInfAfter} days
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="infafter-menu"
            title="Inf Period After"
            variant="light"
            size="sm"
          >
            {infectiousAfterPeriods.map((period) => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedInfAfter === period}
                className="custom-dropdown-item"
              >
                {period}
                {period === DEFAULTS.infAfter ? " days (default)" : " days"}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleContamSelect}
          container={dropdownContainer}
        >
          <Dropdown.Toggle size="sm" className="blue-button" disabled={adm}>
            <ParamDot color={PARAM_COLOURS.contam} />
            <span style={{ fontWeight: 550 }}>Contamination Period</span>
            &nbsp;&nbsp;
            {selectedContam} days
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="contam-menu"
            title="Contamination period"
            variant="light"
            size="sm"
          >
            {contaminationPeriods.map((period) => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedContam === period}
                className="custom-dropdown-item"
                disabled={adm}
              >
                {period}
                {period === DEFAULTS.contam ? " days (default)" : " days"}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleTestTimeBufferSelect}
          container={dropdownContainer}
        >
          <Dropdown.Toggle size="sm" className="blue-button" disabled={adm}>
            <span style={{ fontWeight: 550 }}>Test Time Buffer</span>
            &nbsp;&nbsp;
            {selectedTTB} day(s)
          </Dropdown.Toggle>
          <Dropdown.Menu
            id="TTB-menu"
            title="Test Time Buffer"
            variant="light"
            size="sm"
          >
            {testBufferPeriods.map((period) => (
              <Dropdown.Item
                key={period}
                eventKey={period}
                active={selectedTTB === period}
                className="custom-dropdown-item"
              >
                {period}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div> */}

      <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleEdgeWidth}
          container={dropdownContainer}
        >
          <Dropdown.Toggle className="blue-button" size="sm">
            <span style={{ fontWeight: 550 }}>Edge Width</span>&nbsp;&nbsp;
            {edgeWidthParam === "duration" ? "Overlap duration" : "Uniform"}
          </Dropdown.Toggle>
          <Dropdown.Menu id="edge-width-menu" size="sm">
            <Dropdown.Item
              className="custom-dropdown-item"
              eventKey="None"
              active={edgeWidthParam === "None"}
            >
              Uniform
            </Dropdown.Item>
            <Dropdown.Item
              className="custom-dropdown-item"
              eventKey="duration"
              active={edgeWidthParam === "duration"}
            >
              Overlap duration (default)
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="text-left">
        <Dropdown
          as={ButtonGroup}
          onSelect={handleEdgeColour}
          container={dropdownContainer}
        >
          <Dropdown.Toggle className="blue-button" size="sm">
            <span style={{ fontWeight: 550 }}>Edge Colour</span>&nbsp;&nbsp;
            {edgeColourParam === "None"
              ? "None"
              : edgeColourParam[0].toUpperCase() + edgeColourParam.slice(1)}
          </Dropdown.Toggle>
          <Dropdown.Menu id="edge-colour-menu" size="sm">
            <Dropdown.Item
              className="custom-dropdown-item"
              eventKey="None"
              active={edgeColourParam === "None"}
            >
              None
            </Dropdown.Item>
            <Dropdown.Item
              className="custom-dropdown-item"
              eventKey="ward"
              active={edgeColourParam === "ward"}
            >
              Ward
            </Dropdown.Item>
            <Dropdown.Item
              className="custom-dropdown-item"
              eventKey="Hospital"
              active={edgeColourParam === "Hospital"}
            >
              Hospital
            </Dropdown.Item>
            <Dropdown.Item
              className="custom-dropdown-item"
              eventKey="Specialty"
              active={edgeColourParam === "Specialty"}
            >
              Specialty
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {showGeneticToggle && (
        <div className="text-left">
          <Form.Check
            type="switch"
            id="overlap-constraint-switch"
            className="custom-switch"
            label={
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "purple",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                Show Genetically Related Case(s) Only
                {constraintLoading && (
                  <Spinner
                    as="span"
                    animation="border"
                    role="status"
                    size="sm"
                    style={{ width: "1rem", height: "1rem" }}
                  />
                )}
              </span>
            }
            style={{ paddingTop: "4.5px", marginLeft: "8px" }}
            checked={overlap_constraint}
            disabled={
              tree_exists === undefined ||
              tree_exists?.tree === null ||
              adm ||
              constraintLoading ||
              geneticGraphMicroIds.length === 0
            }
            onChange={handleOverlapConstraintToggle}
          />
        </div>
      )}

      {/* <div className="text-left">
        <Form.Check
          type="switch"
          className="custom-switch"
          id="graph-info-toggle"
          label="Display info on hover"
          checked={graphInfo}
          onChange={handleToggle}
        />
      </div> */}

      <div className="ms-auto">
        <Button
          size="sm"
          variant="outline-primary"
          className="blue-button-flipped"
          onClick={handleResetAll}
        >
          ↺ Reset to default
        </Button>
      </div>
    </Container>
  );
}
